// src/pages/FactoryManagerDashboard/FactoryManagerDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import styles from './FactoryManagerDashboard.module.css';
import notifylogo from '../Assets/bell-icon.svg';
import AppLayout from '../components/AppLayout';
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
} from '../api/notificationApi';
import { formatDateTimeDDMMYYYY } from '../utils/dateFormat';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    factoryManagerDashboardTitle: 'Factory Manager Dashboard',
    welcomeMessage:
      'Welcome to the factory management dashboard. Manage inventory, returns, requests, products, and users from here.',
    add: 'Add',
    remove: 'Remove',
    return: 'Return',
    requests: 'Requests',
    createNewProduct: 'Create New Product',
    manageProducts: 'Manage Products',
    createNewUser: 'Create New User',
    manageUsers: 'Manage Users',
    salesRequests: 'Sales Requests',
    heldOrders: 'Held Orders',
    notifications: 'Notifications',
    noNotifications: 'No notifications yet.',
    markAsRead: 'Mark as Read',
    delete: 'Delete',
    logs: 'Logs',
    taxSettings: 'Tax Settings',
    logout: 'Logout',
    inventoryActionsTitle: 'Inventory Control',
    inventoryActionsSubtitle:
      'Adjust stock levels, record returns, and keep counts accurate.',
    productsCardTitle: 'Product Operations',
    productsCardSubtitle:
      'Create, edit, and review the product catalog and related requests.',
    teamCardTitle: 'Team & Accounts',
    teamCardSubtitle:
      'Manage system access, onboard new users, and stay close to customers.',
    operationsCardTitle: 'Sales & Operations',
    operationsCardSubtitle:
      'Monitor held orders, sales requests, and operational logs.',
    customers: 'Customers',
    settingsCardTitle: 'Settings',
    settingsCardSubtitle: 'Configure system-wide parameters such as the VAT rate.',
  },
  ar: {
    factoryManagerDashboardTitle: 'لوحة إدارة المصنع',
    welcomeMessage:
      'مرحبًا بك في لوحة إدارة المصنع. أدر المخزون والمرتجعات والطلبات والمنتجات والمستخدمين من هنا.',
    add: 'إضافة',
    remove: 'إزالة',
    return: 'إرجاع',
    requests: 'الطلبات',
    createNewProduct: 'إنشاء منتج جديد',
    manageProducts: 'إدارة المنتجات',
    createNewUser: 'إنشاء مستخدم جديد',
    manageUsers: 'إدارة المستخدمين',
    salesRequests: 'طلبات المبيعات',
    heldOrders: 'الطلبات المعلقة',
    notifications: 'الإشعارات',
    noNotifications: 'لا توجد إشعارات حتى الآن.',
    markAsRead: 'تعليم كمقروء',
    delete: 'حذف',
    logs: 'السجلات',
    taxSettings: 'إعدادات الضريبة',
    logout: 'تسجيل الخروج',
    inventoryActionsTitle: 'التحكم في المخزون',
    inventoryActionsSubtitle:
      'اضبط مستويات المخزون وسجّل المرتجعات واحفظ الدقة.',
    productsCardTitle: 'عمليات المنتجات',
    productsCardSubtitle:
      'أنشئ المنتجات وعدّلها واستعرض قائمة الطلبات المرتبطة.',
    teamCardTitle: 'الفريق والحسابات',
    teamCardSubtitle:
      'أدِر صلاحيات النظام، وأضف المستخدمين الجدد، وابق قريبًا من العملاء.',
    operationsCardTitle: 'المبيعات والعمليات',
    operationsCardSubtitle:
      'راقب الطلبات المعلقة، وطلبات المبيعات، وسجلات التشغيل.',
    customers: 'العملاء',
    settingsCardTitle: 'الإعدادات',
    settingsCardSubtitle: 'اضبط المعاملات التشغيلية كمعدل ضريبة القيمة المضافة.',
  },
};

const FactoryManagerDashboard = () => {
  // 2) Language state and translation helper
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  // 3) Toggle language function
  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getNotifications();
        setNotifications(response.data);
        const count = response.data.filter((n) => !n.isRead).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchData();
  }, []);

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      const newCount = notifications.filter((n) => n._id !== id && !n.isRead)
        .length;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      const newCount = notifications.filter((n) => n._id !== id && !n.isRead)
        .length;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleAddClick = () => navigate('/product-management', { state: { action: 'add' } });
  const handleRemoveClick = () => navigate('/product-management', { state: { action: 'remove' } });
  const handleReturnClick = () => navigate('/product-management', { state: { action: 'add' } });
  const handleRequestClick = () => navigate('/requests-management');
  const handleCreateNewProductClick = () => navigate('/create-new-product');
  const handleEditProductClick = () => navigate('/edit-product');
  const handleCreateNewUserClick = () => navigate('/create-new-user');
  const handleManageUsersClick = () => navigate('/manage-users');
  const handleViewSalesRequests = () => navigate('/sales-requests-management');
  const handleViewHeldOrders = () => navigate('/held-orders'); // New Handler for Held Orders
  const handleViewLogs = () => navigate('/logs'); // New Handler for Logs
  const handleCustomer = () => navigate('/customers');
  const handleTaxSettingsClick = () => navigate('/settings/tax');

  const notificationSlot = (
    <div className={styles.notificationWrapper} ref={dropdownRef}>
      <button type="button" className={styles.bellIcon} onClick={toggleDropdown}>
        <img
          src={notifylogo}
          alt="Notification Bell"
          style={{ width: '22px', height: '22px' }}
        />
        {unreadCount > 0 && (
          <span className={styles.unreadBadge}>{unreadCount}</span>
        )}
      </button>
      {showDropdown && (
        <div className={styles.notificationDropdown}>
          <h2 className={styles.notifHeading}>{t('notifications')}</h2>
          {notifications.length === 0 ? (
            <p className={styles.noNotifText}>{t('noNotifications')}</p>
          ) : (
            <div className={styles.notifList}>
              {notifications.map((notif) => (
                <div
                  key={notif._id}
                  className={`${styles.notifItem} ${
                    notif.isRead ? '' : styles.unreadNotif
                  }`}
                >
                  <p className={styles.notifMessage}>{notif.message}</p>
                  <p className={styles.notifDate}>
                    {formatDateTimeDDMMYYYY(notif.createdAt, '-')}
                  </p>
                  <div className={styles.notificationButtons}>
                    {!notif.isRead && (
                      <button
                        type="button"
                        className={styles.markReadButton}
                        onClick={() => handleMarkAsRead(notif._id)}
                      >
                        {t('markAsRead')}
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.DeleteReadButton}
                      onClick={() => handleDeleteNotification(notif._id)}
                    >
                      {t('delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AppLayout
      title={t('factoryManagerDashboardTitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={toggleLanguage}
      showLogout
      logoutLabel={t('logout')}
      notificationSlot={notificationSlot}
      contentClassName={styles.dashboardView}
    >
      <div className={styles.explanationText}>
        <p>{t('welcomeMessage')}</p>
      </div>

      <div className={styles.buttonContainer}>
        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('inventoryActionsTitle')}</h2>
          <p className={styles.cardSummary}>{t('inventoryActionsSubtitle')}</p>
          <div className={styles.cardActions}>
            <button className={styles.actionButton} onClick={handleAddClick}>
              <i className="fas fa-plus"></i> {t('add')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={handleRemoveClick}
            >
              <i className="fas fa-minus"></i> {t('remove')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleReturnClick}
            >
              <i className="fas fa-undo"></i> {t('return')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('productsCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('productsCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.accent}`}
              onClick={handleRequestClick}
            >
              <i className="fas fa-box"></i> {t('requests')}
            </button>
            <button
              className={styles.actionButton}
              onClick={handleCreateNewProductClick}
            >
              <i className="fas fa-plus-circle"></i> {t('createNewProduct')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleEditProductClick}
            >
              <i className="fas fa-edit"></i> {t('manageProducts')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('teamCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('teamCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={styles.actionButton}
              onClick={handleCreateNewUserClick}
            >
              <i className="fas fa-user-plus"></i> {t('createNewUser')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleManageUsersClick}
            >
              <i className="fas fa-users"></i> {t('manageUsers')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.accent}`}
              onClick={handleCustomer}
            >
              <i className="fas fa-user-friends"></i> {t('customers')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('operationsCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('operationsCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleViewSalesRequests}
            >
              <i className="fas fa-chart-line"></i> {t('salesRequests')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.accent}`}
              onClick={handleViewHeldOrders}
            >
              <i className="fas fa-hourglass-half"></i> {t('heldOrders')}
            </button>
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={handleViewLogs}
            >
              <i className="fas fa-clipboard-list"></i> {t('logs')}
            </button>
          </div>
        </article>

        <article className={styles.actionCard}>
          <h2 className={styles.cardHeading}>{t('settingsCardTitle')}</h2>
          <p className={styles.cardSummary}>{t('settingsCardSubtitle')}</p>
          <div className={styles.cardActions}>
            <button
              className={`${styles.actionButton} ${styles.neutral}`}
              onClick={handleTaxSettingsClick}
            >
              <i className="fas fa-percent"></i> {t('taxSettings')}
            </button>
          </div>
        </article>
      </div>
    </AppLayout>
  );
};

export default FactoryManagerDashboard;
